import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookmarkCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import PageHeader from "@/components/PageHeader";
import SoftButton from "@/components/SoftButton";
import { getResults } from "@/lib/storage";
import { QUIZ_QUESTIONS, answerLabel } from "@/lib/quizData";
import { RESULTS } from "@/constants/testIds";

export default function Results() {
  const nav = useNavigate();
  const { id } = useParams();
  const [result, setResult] = useState(null);

  useEffect(() => {
    const all = getResults();
    const found = id ? all.find((r) => r.id === id) : all[0];
    if (!found) {
      nav("/check-in", { replace: true });
      return;
    }
    setResult(found);
  }, [id, nav]);

  if (!result) return null;

  return (
    <PageContainer testid={RESULTS.root}>
      <PageHeader
        title="Your snapshot"
        subtitle={`Saved on ${new Date(result.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`}
        variant="exit"
        exitTo="/home"
        exitLabel="Home"
      />

      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-[2rem] p-6 mb-5"
        style={{ background: "linear-gradient(160deg, rgba(91,57,84,0.98), rgba(109,72,102,0.94))", color: "#FDFBF7" }}
        data-testid={RESULTS.category}
      >
        <p className="text-[11px] uppercase tracking-[0.3em] opacity-70">Private Check-In</p>
        <h2 className="mt-2 text-[27px] leading-tight" style={{ fontFamily: "Lora, serif" }}>
          A snapshot of what you noticed.
        </h2>
        <p className="mt-3 text-[13px] leading-relaxed opacity-80">
          No score, category, or verdict. These answers are here so you can notice what feels true today and see what changes over time.
        </p>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.08 }}
        className="un-card p-5 mb-5"
      >
        <p className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: "var(--un-text-3)" }}>
          What you noticed
        </p>
        <div className="space-y-4">
          {QUIZ_QUESTIONS.map((question) => (
            <div key={question.id}>
              <p className="text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>{question.prompt}</p>
              <p className="mt-1 text-[14px] font-medium" style={{ color: "var(--un-text-1)" }}>
                {answerLabel(result.answers?.[question.id])}
              </p>
            </div>
          ))}
        </div>
      </motion.section>

      <div className="flex flex-col gap-3 mb-6">
        <SoftButton
          data-testid={RESULTS.stepForwardBtn}
          variant="primary"
          size="lg"
          onClick={() => nav("/practices")}
          iconRight={<ArrowRight size={18} />}
          className="w-full"
        >
          Browse a next step
        </SoftButton>
        <div className="grid grid-cols-2 gap-3">
          <SoftButton
            data-testid={RESULTS.saveBtn}
            variant="secondary"
            iconLeft={<BookmarkCheck size={16} />}
            onClick={() => {
              toast.success("Saved with your other reflections.");
              nav("/dashboard");
            }}
          >
            View Saved
          </SoftButton>
          <SoftButton
            data-testid={RESULTS.retakeBtn}
            variant="outline"
            iconLeft={<RotateCcw size={16} />}
            onClick={() => nav("/check-in")}
          >
            Check in again
          </SoftButton>
        </div>
      </div>

      <p className="text-center italic text-[15px] mb-10 px-4 leading-relaxed"
        style={{ color: "var(--un-text-3)", fontFamily: "Cormorant Garamond, serif", fontSize: "17px" }}>
        This is a moment in time, not a definition of you.
      </p>
    </PageContainer>
  );
}
